import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, ShieldAlert, Pencil, Plus, CheckCircle2, XCircle } from "lucide-react";
import type { ContractorPayProfile } from "@/data/contractorPayProfile";

interface Props {
  profile?: ContractorPayProfile;
  onEdit: () => void;
  onCreate: () => void;
  compact?: boolean;
}

/**
 * Installer Eligibility Profile card.
 *
 * Displays governance fields only (allowed specs, tier, active state).
 * Per-job payout amounts are NOT shown here — they live on the offer
 * and surface in the installer portal payout section.
 */
export function PayProfileCard({ profile, onEdit, onCreate, compact }: Props) {
  if (!profile) {
    return (
      <Card className="shadow-none border-destructive/40 bg-destructive/[0.04]">
        <CardContent className={compact ? "p-3" : "p-4"}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2.5 min-w-0">
              <ShieldAlert className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-bold text-destructive">Eligibility Profile Required</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Installer can't be assigned to any job until coverage and spec eligibility are configured.
                </p>
              </div>
            </div>
            <Button size="sm" onClick={onCreate} className="shrink-0">
              <Plus className="h-3.5 w-3.5 mr-1" /> Create Eligibility Profile
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const updated = profile.updatedAt
    ? new Date(profile.updatedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : "—";

  return (
    <Card className="shadow-none">
      <CardContent className={compact ? "p-3" : "p-4"}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em] flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" /> Eligibility Profile
            {!profile.isActive && (
              <Badge variant="outline" className="bg-destructive/15 text-destructive text-[10px]">Inactive</Badge>
            )}
            {profile.tierLevel === "preferred" && (
              <Badge variant="outline" className="bg-primary/15 text-primary text-[10px]">Preferred</Badge>
            )}
          </h2>
          <Button size="sm" variant="ghost" onClick={onEdit} className="h-7 px-2">
            <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
          </Button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
          <div className="p-2.5 rounded-lg bg-muted/30 border">
            <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Allowed Specs</p>
            <p className="text-base font-bold mt-0.5">{profile.allowedSpecTypes.length}</p>
          </div>
          <div className="p-2.5 rounded-lg bg-muted/30 border">
            <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Tier</p>
            <p className="text-xs font-bold mt-0.5 capitalize">{profile.tierLevel ?? "standard"}</p>
          </div>
          <div className="p-2.5 rounded-lg bg-muted/30 border">
            <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Status</p>
            <p className="text-xs font-bold mt-0.5 flex items-center gap-1">
              {profile.isActive ? (
                <><CheckCircle2 className="h-3 w-3 text-emerald-600" /> Active</>
              ) : (
                <><XCircle className="h-3 w-3 text-destructive" /> Inactive</>
              )}
            </p>
          </div>
          <div className="p-2.5 rounded-lg bg-muted/30 border">
            <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Updated</p>
            <p className="text-xs font-bold mt-0.5">{updated}</p>
          </div>
        </div>

        <div>
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.12em] mb-1.5">
            Allowed Spec Types ({profile.allowedSpecTypes.length})
          </p>
          <div className="flex flex-wrap gap-1.5">
            {profile.allowedSpecTypes.map((s) => (
              <span key={s} className="text-[10px] font-mono font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                {s}
              </span>
            ))}
          </div>
        </div>

        {profile.notes && (
          <p className="text-[11px] text-muted-foreground mt-3 p-2 rounded bg-muted/20 border">{profile.notes}</p>
        )}

        <p className="text-[10px] text-muted-foreground mt-3 italic">
          Payout amounts are set per offer, not on this profile.
        </p>
      </CardContent>
    </Card>
  );
}
